import styled, { keyframes } from 'styled-components';
// ============================================
// KEYFRAME ANIMATIONS
// ============================================
const fadeUp = keyframes `
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
// ============================================
// PAGE LAYOUT
// ============================================
export const PageWrapper = styled.div `
  min-height: 100vh;
  background: var(--bg-primary);
`;
// ============================================
// HERO SECTION
// ============================================
export const HeroSection = styled.section `
  padding: 48px 24px 32px;
  text-align: center;
  background: linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
  border-bottom: 1px solid var(--border-color);
`;
export const HeroTitle = styled.h1 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 38px !important;
  font-weight: 600 !important;
  letter-spacing: 0.03em;
  margin-bottom: 10px !important;
  color: var(--text-primary) !important;

  span {
    color: var(--accent-gold, #B8860B);
  }

  @media (max-width: 768px) {
    font-size: 28px !important;
  }
`;
export const HeroSubtitle = styled.p `
  color: var(--text-secondary);
  font-size: 15px;
  max-width: 520px;
  margin: 0 auto;
  line-height: 1.6;
`;
// ============================================
// STATS GRID
// ============================================
export const StatsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  max-width: 1200px;
  margin: -24px auto 32px;
  padding: 0 24px;
  position: relative;
  z-index: 10;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    margin-top: -16px;
  }
`;
export const StatCard = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 24px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #B8860B), var(--accent-gold-light, #D4A634));
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
    border-color: var(--accent-gold-border, rgba(184, 134, 11, 0.25));

    &::before {
      opacity: 1;
    }
  }

  .stat-value {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 32px;
    font-weight: 600;
    color: var(--accent-gold, #B8860B);
    margin-bottom: 8px;
  }

  .stat-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--text-secondary);
  }
`;
// ============================================
// MAIN CONTENT
// ============================================
export const MainContent = styled.div `
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px 48px;
`;
// ============================================
// SECTION COMPONENTS
// ============================================
export const Section = styled.section `
  margin-bottom: 40px;
`;
export const SectionTitle = styled.h2 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 20px !important;
  font-weight: 600 !important;
  color: var(--text-primary) !important;
  margin: 0 0 20px 0 !important;
  display: flex;
  align-items: center;
  gap: 10px;

  span {
    color: var(--accent-gold, #B8860B);
    font-size: 16px;
  }
`;
// ============================================
// RISK LEGEND
// ============================================
export const RiskLegend = styled.div `
  display: flex;
  gap: 24px;
  padding: 20px 24px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  margin-bottom: 32px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 16px;
  }
`;
export const LegendItem = styled.div `
  display: flex;
  align-items: center;
  gap: 10px;
`;
export const LegendColor = styled.div `
  width: 18px;
  height: 18px;
  background: ${({ $color }) => $color};
  border: 1px solid ${({ $color }) => $color};
`;
export const LegendLabel = styled.span `
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;
// ============================================
// FILTER SECTION
// ============================================
export const FilterBar = styled.div `
  display: flex;
  gap: 16px;
  padding: 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  margin-bottom: 32px;
  flex-wrap: wrap;
  align-items: flex-end;
`;
export const FilterGroup = styled.div `
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 180px;

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;
export const FilterLabel = styled.label `
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;
export const FilterSelect = styled.select `
  padding: 12px 16px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #B8860B);
    box-shadow: 0 0 0 3px var(--accent-gold-bg, rgba(184, 134, 11, 0.08));
  }

  &:hover {
    border-color: var(--accent-gold-border, rgba(184, 134, 11, 0.25));
  }

  option {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }
`;
export const CheckboxLabel = styled.label `
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--text-primary);
  cursor: pointer;
  padding: 12px 0;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: var(--accent-gold, #B8860B);
    cursor: pointer;
  }
`;
// ============================================
// COMPARISON CARDS
// ============================================
export const ComparisonGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;
export const ComparisonCard = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 20px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${({ $variant }) => $variant === 'added' ? '#22c55e' :
    $variant === 'removed' ? '#ef4444' :
        'var(--border-color)'};
  }
`;
export const ComparisonTitle = styled.h4 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  margin: 0 0 16px 0 !important;
  color: ${({ $variant }) => $variant === 'added' ? '#22c55e' :
    $variant === 'removed' ? '#ef4444' :
        'var(--text-tertiary)'} !important;
`;
export const ComparisonItem = styled.div `
  font-size: 12px;
  margin-bottom: 8px;
  color: ${({ $variant }) => $variant === 'added' ? '#22c55e' :
    $variant === 'removed' ? '#ef4444' :
        'var(--text-tertiary)'};

  &::before {
    content: '${({ $variant }) => $variant === 'added' ? '+' : $variant === 'removed' ? '-' : '•'}';
    margin-right: 8px;
    font-weight: bold;
  }
`;
export const ComparisonMore = styled.div `
  font-size: 11px;
  color: var(--text-tertiary);
  margin-top: 12px;
  font-style: italic;
`;
// ============================================
// PERMISSION MATRIX
// ============================================
export const MatrixWrapper = styled.div `
  overflow-x: auto;
  margin-bottom: 20px;
  border: 1px solid var(--border-color);
`;
export const MatrixTable = styled.table `
  width: 100%;
  border-collapse: collapse;
  background: var(--bg-secondary);

  th {
    background: var(--bg-tertiary);
    color: var(--text-primary);
    padding: 14px 10px;
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border: 1px solid var(--border-color);
    position: sticky;
    top: 0;
    z-index: 1;
  }

  td {
    padding: 10px;
    border: 1px solid var(--border-color);
    font-size: 13px;
    vertical-align: middle;
  }

  tbody tr {
    transition: background 0.2s;

    &:hover {
      background: var(--bg-tertiary);
    }
  }
`;
export const PermissionName = styled.div `
  color: var(--text-primary);
  font-weight: 500;
  min-width: 200px;
`;
export const PermissionDescription = styled.div `
  color: var(--text-tertiary);
  font-size: 11px;
  margin-top: 4px;
`;
export const RoleHeader = styled.th `
  writing-mode: vertical-rl;
  text-orientation: mixed;
  min-width: 50px;
  text-align: center !important;
  padding: 16px 8px !important;
`;
export const PermissionCell = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  min-height: 32px;
  transition: all 0.2s;

  ${({ $hasPermission, $isDirect, $riskLevel }) => {
    if (!$hasPermission) {
        return `
        background: var(--bg-tertiary);
        color: var(--text-muted);
      `;
    }
    const riskColors = {
        low: '#22c55e',
        medium: '#f59e0b',
        high: '#ef4444',
        critical: '#dc2626'
    };
    const color = riskColors[$riskLevel];
    const bgOpacity = $isDirect ? '25' : '15';
    return `
      background: ${color}${bgOpacity};
      color: ${color};
      border: 1px solid ${color}40;
    `;
}}

  ${({ $isFocusRole }) => $isFocusRole && `
    box-shadow: inset 0 0 0 2px var(--accent-gold, #B8860B);
  `}
`;
export const PermissionIcon = styled.span `
  font-size: 14px;
  font-weight: bold;
`;
export const PermissionBadge = styled.span `
  font-size: 8px;
  padding: 2px 4px;
  margin-left: 4px;
  font-weight: 700;
  background: ${({ $type }) => $type === 'focus' ? '#22c55e' : 'rgba(255, 255, 255, 0.2)'};
  color: ${({ $type }) => $type === 'focus' ? '#FFFFFF' : 'inherit'};
`;
export const MatrixLegend = styled.div `
  padding: 16px 20px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-top: none;
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.8;

  strong {
    color: var(--text-primary);
  }
`;
// ============================================
// CATEGORY BADGE
// ============================================
export const CategoryBadge = styled.span `
  display: inline-block;
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: var(--accent-gold-bg, rgba(184, 134, 11, 0.08));
  color: var(--accent-gold, #B8860B);
  border: 1px solid var(--accent-gold-border, rgba(184, 134, 11, 0.25));
`;
export const RiskBadge = styled.span `
  display: inline-block;
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${({ $level }) => {
    const colors = {
        low: { bg: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', border: 'rgba(34, 197, 94, 0.3)' },
        medium: { bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', border: 'rgba(245, 158, 11, 0.3)' },
        high: { bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: 'rgba(239, 68, 68, 0.3)' },
        critical: { bg: 'rgba(220, 38, 38, 0.1)', color: '#dc2626', border: 'rgba(220, 38, 38, 0.3)' }
    };
    const c = colors[$level];
    return `
      background: ${c.bg};
      color: ${c.color};
      border: 1px solid ${c.border};
    `;
}}
`;
// ============================================
// ROLE ANALYSIS GRID
// ============================================
export const RoleAnalysisGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;
export const RoleCard = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #B8860B), var(--accent-gold-light, #D4A634));
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover {
    border-color: var(--accent-gold-border, rgba(184, 134, 11, 0.25));
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);

    &::before {
      opacity: 1;
    }
  }
`;
export const RoleCardTitle = styled.h4 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  color: var(--text-primary) !important;
  margin: 0 0 12px 0 !important;
`;
export const RoleCardStats = styled.div `
  font-size: 12px;
  color: var(--text-tertiary);
  margin-bottom: 12px;
`;
export const RiskTagsContainer = styled.div `
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;
export const RiskTag = styled.span `
  font-size: 10px;
  padding: 4px 8px;
  font-weight: 600;
  color: #FFFFFF;

  ${({ $level }) => {
    const colors = {
        low: '#22c55e',
        medium: '#f59e0b',
        high: '#ef4444',
        critical: '#dc2626'
    };
    return `background: ${colors[$level]};`;
}}
`;
export const RoleCardBreakdown = styled.div `
  font-size: 11px;
  color: var(--text-tertiary);
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--border-color);
`;
// ============================================
// ACTION BUTTONS
// ============================================
export const ActionsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;
export const SecondaryButton = styled.button `
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  padding: 14px 24px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent-gold, #B8860B);
    color: var(--accent-gold, #B8860B);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
